<template>
  <WdsModal
    :show="show"
    :title="title"
    :custom-size="hasRail ? 'min(1200px, 100%)' : undefined"
    @cancel="$emit('close')"
  >
    <template v-if="subtitle && !hasRail" #header>
      <WdsStack direction-column gap-xs>
        <WdsText xl2 class="tw-font-normal">{{ title }}</WdsText>
        <WdsText sm secondary class="tw-tabular-nums" data-test="compliance-review-subtitle">
          {{ subtitle }}
        </WdsText>
      </WdsStack>
    </template>

    <div
      :class="{
        'tw--mx-6 tw--mt-4 tw-border-0 tw-border-t tw-border-solid tw-border-gray-200': hasRail,
      }"
    >
      <div v-if="hasRail" class="tw-px-6 tw-py-4 min-[960px]:tw-hidden">
        <WdsSegmentedControl
          full-width
          :options="paneOptions"
          :default-active-key="pane"
          data-test="compliance-review-pane-switch"
          @click="pane = $event as 'main' | 'rail'"
        />
      </div>

      <div :class="{ 'tw-flex tw-h-[65vh] tw-flex-col min-[960px]:tw-flex-row': hasRail }">
        <div
          :class="{
            'tw-box-border tw-h-full tw-min-w-0 tw-flex-1 tw-overflow-y-auto tw-px-6 tw-py-5': hasRail,
            'tw-hidden min-[960px]:tw-block': hasRail && pane === 'rail',
          }"
          data-test="compliance-review-main"
        >
          <div v-if="loading" class="tw-flex tw-justify-center tw-py-8">
            <WdsSpinner :size="24" indeterminate />
          </div>

          <template v-else>
            <slot name="summary" />

            <WdsStack
              v-if="applicableCompliances.length === 0 && !$slots.summary"
              direction-column
              class="tw-py-4"
            >
              <WdsText :text="emptyMessage" secondary />
            </WdsStack>

            <WdsStack v-else-if="applicableCompliances.length > 0" direction-column gap-none>
              <WdsStack gap-md direction-column>
                <template v-for="(comp, index) in applicableCompliances" :key="comp.name">
                  <ComplianceSection :row="comp" />
                  <WdsDivider v-if="index < applicableCompliances.length - 1" />
                </template>
              </WdsStack>
            </WdsStack>
          </template>
        </div>

        <div
          v-if="hasRail"
          :class="{ 'tw-hidden min-[960px]:tw-block': pane === 'main' }"
          class="tw-box-border tw-h-full tw-overflow-y-auto tw-border-0 tw-border-l tw-border-solid tw-border-gray-200 tw-bg-true-gray-50 tw-p-5 min-[960px]:tw-w-1/2 min-[960px]:tw-flex-none"
          data-test="compliance-review-rail"
        >
          <slot name="rail" />
        </div>
      </div>
    </div>

    <template #footer>
      <div
        :class="{
          'tw--mx-6 tw--mb-6 tw--mt-7 tw-border-0 tw-border-t tw-border-solid tw-border-gray-200 tw-px-6 tw-py-5': hasRail,
        }"
      >
        <slot name="footer">
          <WdsButton tertiary :data-test="cancelDataTest" @click="$emit('close')">
            {{ $t('Cancel') }}
          </WdsButton>
        </slot>
      </div>
    </template>
  </WdsModal>
</template>

<script lang="ts" setup>
import { computed, ref, useSlots } from 'vue'
import {
  WdsButton,
  WdsDivider,
  WdsModal,
  WdsSegmentedControl,
  WdsSpinner,
  WdsStack,
  WdsText,
} from '@worksome/wds-components'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import ComplianceSection from '@/components/compliances/ComplianceReviewModal/ComplianceSection.vue'
import { FragmentType } from '@/gql'
import { ComplianceSectionFragmentDoc } from '@/gql/graphql'

type ComplianceRow = FragmentType<typeof ComplianceSectionFragmentDoc> & { name: string }

const props = defineProps<{
  show: boolean
  title: string
  // Shown under the title when there is no rail to hold it.
  subtitle?: string
  loading: boolean
  applicableCompliances: ReadonlyArray<ComplianceRow>
  emptyMessage: string
  cancelDataTest: string
  // Name the panes on the switch used below 960px, where only one pane shows.
  mainTitle?: string
  railTitle?: string
}>()

defineEmits<{ close: [] }>()

const slots = useSlots()

const hasRail = computed(() => !!slots.rail)

const pane = ref<'main' | 'rail'>('main')

const paneOptions = computed(() => [
  { optionKey: 'main', title: props.mainTitle ?? $t('Compliance') },
  { optionKey: 'rail', title: props.railTitle ?? '' },
])
</script>
